import React, { useRef, useState } from 'react'
import invariant from 'tiny-invariant'
import { useMutation } from '@tanstack/react-query'
import { PickedMeetingLocation } from '@chilipiper/api-client/src/chat-platform-chat'
import {
  AvailabilityOptions,
  buildReserveTimeSlotPayload,
  useReservationSlot,
  useSlots,
} from '@chilipiper/booking-flow'
import { useMeeting } from '../../../api-queries/chat-platform-chat'
import { BookMeetingBlock as BookMeetingBlockType } from '../../../chat-data/types'
import { useMessageFeedContext } from '../../../chat-widget/MessageFeedContext'
import { useMeetingTypeResolution } from '../../../api-queries/useMeetingTypeResolution'
import { chatPublicEventAction, chiliChatEventName } from '../../../constants'
import { transformMeetingLocation } from './helpers'
import { BookingFlowBlock } from './BookingFlowBlock'
import { ExpiredBooking } from './ExpiredBooking'
import { BookingFlowBlockLoading } from './BookingFlowBlockLoading'

interface Props {
  block: BookMeetingBlockType
}

export const BookMeetingBlock = ({ block }: Props) => {
  if (block.cancelled) {
    return <ExpiredBooking />
  }
  return <BookMeetingBlockNotCancelled block={block} />
}

const BookMeetingBlockNotCancelled = ({ block }: Props) => {
  const { selectMeetingSlot, tenantId = '' } = useMessageFeedContext()
  const {
    meetingType,
    meetingTypeId,
    isLoading: isLoadingMeetingType,
  } = useMeetingTypeResolution(tenantId, block.meetingTypeId)
  const now = useRef(new Date())
  const [isRescheduling, setIsRescheduling] = useState(false)
  const availabilityOptions: AvailabilityOptions = (() => {
    if (block.distributionId) {
      return {
        distributionAttendees: [
          {
            userIds: block.assignees,
            distributionId: block.distributionId,
            required: true,
          },
        ],
        expectedHost: {
          type: 'AssigneeFromDistribution' as const,
          distributionId: block.distributionId,
        },
        ownershipAndUsersAttendees: [],
      }
    }
    return {
      userIds: block.assignees,
      meetingLimit: { type: 'NoMeetingLimit' as const },
      expectedHost: { type: 'User' as const, userId: block.assignees[0] as string },
    }
  })()

  const {
    data: timeSlots,
    refetchSlots: refetchAvailability,
    isLoading,
  } = useSlots({
    meetingTypeVersion: now.current.toISOString(),
    availabilityOptions,
    tenantId,
    intervalDuration: '21 days',
    meetingTypeId: meetingType?.id ?? meetingTypeId,
  })

  const { data: meeting } = useMeeting(tenantId, block.meetingId)

  const {
    mutate: selectSlot,
    isPending: isSelectingSlot,
    isSuccess: isSlotSelected,
  } = useMutation({
    mutationFn: async (data: Parameters<typeof selectMeetingSlot>[0]) =>
      selectMeetingSlot(data) ?? null,
  })

  const { onReserveTimeSlot, isLoadingReservation: isReservingTimeSlot } = useReservationSlot()

  const bookMeetingThroughWidget = ({
    reservationId,
    duration,
    selectedTime,
    email,
    timezone,
    assigneeIds,
  }: {
    duration: string
    email: string
    reservationId?: string
    selectedTime: string
    timezone: string
    assigneeIds: string[]
  }) => {
    const commonData = {
      email,
      timezone,
      interval: { duration, startsAt: selectedTime },
      pickedMeetingLocation: meetingType?.meetingInvite.locations.default
        ? transformMeetingLocation(meetingType.meetingInvite.locations.default)
        : ({ type: 'DefinedInMeetingType' } satisfies PickedMeetingLocation),
    } as const

    selectSlot(
      reservationId && assigneeIds[0]
        ? {
            ...commonData,
            type: 'MeetingSlotSelectedWithReservation',
            assigneeId: assigneeIds[0],
            reservationId,
          }
        : {
            ...commonData,
            type: 'MeetingSlotSelectedWithoutReservation',
            assigneeIds,
          }
    )
  }

  if (isLoadingMeetingType) {
    return <BookingFlowBlockLoading meetingId={block.meetingId} />
  }

  return (
    <BookingFlowBlock
      isRescheduling={isRescheduling}
      onReschedulingChange={setIsRescheduling}
      availabilityOptions={availabilityOptions}
      claimMeetingAttemptReservationError={{}}
      fields={[
        {
          dataType: {
            type: 'Email',
          },
          id: 'PersonEmail',
          isRequired: true,
          label: 'Email',
        },
      ]}
      meetingInvite={meetingType?.meetingInvite}
      block={block}
      onBookMeeting={({ slot, onError, timezone, guestFormData }) => {
        const guestFormEmail = guestFormData['PersonEmail']
        const duration = meetingType?.meetingInvite.duration
        const email = Array.isArray(guestFormEmail) ? guestFormEmail[0] : guestFormEmail
        if (!email || !duration) {
          return
        }

        window.dispatchEvent(
          new CustomEvent(chiliChatEventName, {
            detail: {
              action: chatPublicEventAction.TIMESLOT_SELECTED,
              args: { time: slot, timezone, duration },
            },
          })
        )

        // this means we shouldn't reserve slots as there are multiple assignees
        if (block.assignees.length !== 1) {
          return bookMeetingThroughWidget({
            email,
            duration,
            selectedTime: slot,
            timezone,
            assigneeIds: block.assignees,
          })
        }

        const users = (() => {
          if (block.distributionId) {
            const distributionId = block.distributionId
            return {
              type: 'DistributionUsers' as const,
              attendeesFromDistribution: block.assignees.map(assignee => ({
                userId: assignee,
                distributionId,
              })),
            }
          }
          return {
            type: 'UsersWithLimit' as const,
            limit: { type: 'NoMeetingLimit' as const },
            userIds: block.assignees,
          }
        })()

        onReserveTimeSlot({
          tenantId,
          payload: buildReserveTimeSlotPayload({
            meetingTypeRef: { id: meetingTypeId, timestamp: now.current.toISOString() },
            slotStartsAt: slot,
            users,
          }),
        })
          .then(reservation => {
            invariant(reservation, 'Reservation not found')
            window.dispatchEvent(
              new CustomEvent(chiliChatEventName, {
                detail: {
                  action: chatPublicEventAction.BOOKED,
                  args: { reservationId: reservation.id },
                },
              })
            )
            bookMeetingThroughWidget({
              reservationId: reservation.id,
              duration,
              selectedTime: slot,
              email,
              timezone,
              assigneeIds: block.assignees,
            })
          })
          .catch(() => {
            onError()
          })
      }}
      isMeetingReserved={isSlotSelected}
      isBookingMeeting={isReservingTimeSlot || isSelectingSlot}
      guestEmail={block.email ?? meeting?.primaryGuest.value}
      isLoading={isLoading}
      initialTimezone={
        meetingType?.forceGuestTimezone.type === 'Enabled'
          ? meetingType.forceGuestTimezone.zoneId
          : undefined
      }
      error={block.errorCode}
      refetchAvailability={refetchAvailability}
      timeSlots={timeSlots}
    />
  )
}
